import React, { useReducer, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { useManagedQuery } from 'utils/managedHooks';
import { Spinner } from 'admin-components/components';
import { Column } from 'components/shared/Layout';
import { GET_LUGGAGE_PRODUCTS } from 'components/shared/AddAssistance/queries';
import { transformLuggageToExtras } from 'components/shared/AddAssistance/AddAssistanceForm/utils';
import { reducer, getInitialState } from 'components/shared/AddAssistance/AddAssistanceForm/AddAssistanceForm.reducer';
import {
  onToggleAssistanceVisibilityAction,
  onRemoveAllItemsAction,
  onRemoveItemAction,
  onAddItemAction,
  onEditItemAction
} from 'components/shared/AddAssistance/AddAssistanceForm/AddAssistanceForm.actions';
import AddAssistanceOptions from 'components/shared/AddAssistance/AddAssistanceForm/AddAssistanceOptions';
import { StyledMessage, StyledGenericMessage } from 'components/shared/AddAssistance/AddAssistance.styles';
import { FormContainer } from './ExtrasOnlyBooking.styles';
import Button from 'components/ui/Button';

const buildJourneyOption = (id) => ({ id, translationId: `addAssistance.journey.${id}` });

const RESET_STATE_ACTION = 'RESET_STATE';

const HomogeneousLuggageExtrasSection = ({ currency, booking, passengers, onStateChange }) => {
  const [showLuggageExtras, setShowLuggageExtras] = useState(false);

  const outbound = useMemo(() => booking?.eurostarOutbound || [], [booking?.eurostarOutbound]);
  const inbound = useMemo(() => booking?.eurostarInbound || [], [booking?.eurostarInbound]);
  const allBookingItems = booking?.allBookingItems || [];
  const uncanceledOutbound = outbound.filter(({ cancelled }) => !cancelled) ?? [];
  const uncanceledInbound = inbound.filter(({ cancelled }) => !cancelled) ?? [];
  const bothDirection = uncanceledOutbound?.length > 0 && uncanceledInbound?.length > 0;
  const outboundOnly = uncanceledOutbound?.length > 0 && uncanceledInbound?.length === 0;
  const isReturn = bothDirection;

  const formatPassengers = () =>
    (passengers || []).map((p) => ({
      id: p.id,
      type: p.type,
      disabilityType: p.disabilityType
    }));

  const [state, dispatch] = useReducer((currentState, action) => {
    if (action.type === RESET_STATE_ACTION) {
      return action.payload;
    }
    return reducer(currentState, action);
  }, getInitialState([], [], [], passengers, isReturn, ''));

  const { data: luggageProducts, loading: luggageProductsLoading } = useManagedQuery(GET_LUGGAGE_PRODUCTS, {
    variables: {
      currency,
      exchangeItems: allBookingItems
        .filter(({ cancelled, canBeRebooked }) => !cancelled && canBeRebooked)
        .map(({ itemRef }) => Number(itemRef)),
      reference: booking?.reference || '',
      origin: bothDirection || outboundOnly ? uncanceledOutbound?.[0]?.origin?.uic : uncanceledInbound?.[0]?.origin?.uic,
      destination:
        bothDirection || outboundOnly
          ? uncanceledOutbound?.[uncanceledOutbound?.length - 1]?.destination?.uic
          : uncanceledInbound?.[uncanceledInbound?.length - 1]?.destination?.uic,
      passengers: formatPassengers(),
      ...(uncanceledOutbound?.length > 0 && { outboundDate: uncanceledOutbound?.[0]?.timing?.date }),
      ...(uncanceledInbound?.length > 0 && { inboundDate: uncanceledInbound?.[0]?.timing?.date }),
      trainNumbers: Array.from(new Set([...uncanceledOutbound.map((o) => o.trainNumber), ...uncanceledInbound.map((o) => o.trainNumber)]))
    },
    fetchPolicy: 'network-only',
    skip: !booking || !showLuggageExtras,
    onCompleted: (data) => {
      if (data) {
        const transformedExtras = transformLuggageToExtras(data, outbound, inbound);
        const extrasOptions = [...(transformedExtras.outbound || []), ...(transformedExtras.inbound || [])];
        const newState = getInitialState([], [], extrasOptions, passengers, isReturn, '');
        dispatch({ type: RESET_STATE_ACTION, payload: { ...newState, transformedLuggageExtras: transformedExtras } });
      }
    }
  });

  const transformedLuggageExtras = useMemo(
    () =>
      luggageProducts
        ? transformLuggageToExtras(luggageProducts, outbound, inbound)
        : {
            outbound: [],
            inbound: []
          },
    [luggageProducts, outbound, inbound]
  );

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        ...state,
        transformedLuggageExtras
      });
    }
  }, [state, transformedLuggageExtras, onStateChange]);

  return (
    <>
      {!showLuggageExtras ? (
        <Button
          type="button"
          onClick={() => setShowLuggageExtras(true)}
          data-cy="add-luggage-extras-button"
          disabled={!booking?.allBookingItems?.some((item) => !item.cancelled && item.canBeRebooked)}
        >
          <FormattedMessage id="extrasOnlyBookings.addNewExtra" />
        </Button>
      ) : luggageProductsLoading ? (
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      ) : !transformedLuggageExtras.outbound.length && !transformedLuggageExtras.inbound.length ? (
        <StyledMessage>
          <FormattedMessage id="addExtras.noAvailability" />
        </StyledMessage>
      ) : (
        <FormContainer>
          <h3>
            <FormattedMessage id="addAssistance.journey.outbound" />
          </h3>
          {transformedLuggageExtras.outbound.length > 0 ? (
            transformedLuggageExtras.outbound.map((extra) => {
              const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[extra.id] || {
                isAddPassengersDisabled: false,
                isVisible: false,
                items: [],
                isIncomplete: false
              };

              return (
                <AddAssistanceOptions
                  currency={currency}
                  key={extra.id}
                  isIncomplete={isIncomplete}
                  assistance={extra}
                  isExtra
                  directions={[buildJourneyOption(extra.direction)]}
                  passengers={passengers || []}
                  isAddPassengersDisabled={isAddPassengersDisabled}
                  isItemsVisible={isVisible}
                  items={items}
                  onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(extra.id))}
                  onAddItem={() => dispatch(onAddItemAction(extra.id))}
                  onChangeItem={(item) => dispatch(onEditItemAction({ key: extra.id, item }))}
                  onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(extra.id))}
                  onRemoveItem={(item) =>
                    dispatch(
                      onRemoveItemAction({
                        key: extra.id,
                        item
                      })
                    )
                  }
                />
              );
            })
          ) : (
            <StyledMessage>
              <FormattedMessage id="addExtras.noAvailability" />
            </StyledMessage>
          )}
          {uncanceledInbound?.length > 0 && (
            <>
              <h3>
                <FormattedMessage id="addAssistance.journey.inbound" />
              </h3>
              {transformedLuggageExtras.inbound.length > 0 ? (
                transformedLuggageExtras.inbound.map((extra) => {
                  const { isAddPassengersDisabled, isVisible, items, isIncomplete } = state.assistance[extra.id] || {
                    isAddPassengersDisabled: false,
                    isVisible: false,
                    items: [],
                    isIncomplete: false
                  };

                  return (
                    <AddAssistanceOptions
                      currency={currency}
                      key={extra.id}
                      isIncomplete={isIncomplete}
                      assistance={extra}
                      isExtra
                      directions={[buildJourneyOption(extra.direction)]}
                      passengers={passengers || []}
                      isAddPassengersDisabled={isAddPassengersDisabled}
                      isItemsVisible={isVisible}
                      items={items}
                      onToggleItemsVisibility={() => dispatch(onToggleAssistanceVisibilityAction(extra.id))}
                      onAddItem={() => dispatch(onAddItemAction(extra.id))}
                      onChangeItem={(item) => dispatch(onEditItemAction({ key: extra.id, item }))}
                      onRemoveAllItems={() => dispatch(onRemoveAllItemsAction(extra.id))}
                      onRemoveItem={(item) =>
                        dispatch(
                          onRemoveItemAction({
                            key: extra.id,
                            item
                          })
                        )
                      }
                    />
                  );
                })
              ) : (
                <StyledMessage>
                  <FormattedMessage id="addExtras.noAvailability" />
                </StyledMessage>
              )}
            </>
          )}
        </FormContainer>
      )}
      {state.isAssistanceIncomplete && booking && (
        <StyledGenericMessage data-cy="luggage-extras-warning" data-testid="luggage-extras-warning">
          <FormattedMessage id="addAssistance.generalWarning" />
        </StyledGenericMessage>
      )}
    </>
  );
};

HomogeneousLuggageExtrasSection.propTypes = {
  currency: PropTypes.string.isRequired,
  booking: PropTypes.object,
  passengers: PropTypes.array.isRequired,
  onStateChange: PropTypes.func
};

export default HomogeneousLuggageExtrasSection;
